import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import classNames from "classnames";
import { SizeInformation } from "client_legacy/ndaw/sizing";
import {
	EffectiveTheme,
	useEffectiveTheme,
	useValueEqualMemo
} from "client/Hooks";
import {
	IFrame,
	IFramePermission,
	NoResults
} from "client/Components";
import { urlEncodeObject } from "client_legacy/ndaw/urlHelper";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import styles from "./UserHtml.module.less";
import {
	ensureNotNil,
	setTrackingId,
	waitForAnimationFrames
} from "client/Misc/utils";
import { Coordinate } from "client/Misc/commonTypes";
import { userConfiguration, UserHtmlModel } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";

export type UserHtmlSizeKind = "stretch" | "content-size";

export type UserHtmlSize = {
	horizontal: UserHtmlSizeKind;
	vertical: UserHtmlSizeKind;
};

export type UserHtmlProps = {
	html: string;
	size?: UserHtmlSizeKind | UserHtmlSize;
	trackingId: string;
};

export const UserHtml = (props: UserHtmlProps): ReactElement => {
	const {
		html,
		size = "content-size",
		trackingId
	} = props;

	const model = useMemo(
		(): UserHtmlModel => userConfiguration().userHtml,
		[]
	);

	const configuredSize: UserHtmlSize = useValueEqualMemo(
		(): UserHtmlSize => {
			return typeof size === "string"
				? {
					horizontal: size,
					vertical: size
				}
				: size;
		},
		[size]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={classNames(
				styles.root,
				{
					[styles.root_stretchedX]: configuredSize.horizontal === "stretch",
					[styles.root_stretchedY]: configuredSize.vertical === "stretch"
				}
			)}
		>
			{!model.isConfigured
				? (
					<NoResults
						title={localize("_EmbedContentNotEnabled")}
						message={localize("_PleaseContactYourAdminToEnableEmbeddedContent")}
						height="fill"
						icon="gear"
						trackingId="not-configured"
					/>
				)
				: (
					<UserHtmlIframe
						configuredSize={configuredSize}
						hostUrl={model.url}
						html={html}
						trackingId="iframe"
					/>
				)
			}
		</div>
	);
};

UserHtml.displayName = "UserHtml";

type UserHtmlIframeProps = {
	hostUrl: string;
	html: string;
	configuredSize: UserHtmlSize;
	trackingId: string;
};

const UserHtmlIframe = (props: UserHtmlIframeProps): ReactElement => {
	const {
		hostUrl,
		html,
		configuredSize,
		trackingId
	} = props;

	const [contentSize, setContentSize] = useState<Partial<SizeInformation>>({});
	const ref: RefObject<HTMLIFrameElement | null> = useRef<HTMLIFrameElement>(null);
	const theme: EffectiveTheme = useEffectiveTheme();
	const forceReloadTrackerRef: RefObject<ForceReloadTrackerState | null> = useRef<ForceReloadTrackerState>(null);

	useUnrequestedFocusGuard(ref);

	const { src, origin } = useMemo(
		(): { src: string; origin: string; } => {
			const queryString: string = urlEncodeObject({
				culture: getCurrentCulture(),
				theme: theme,
				height: configuredSize.vertical,
				width: configuredSize.horizontal
			});

			const url = new URL(hostUrl);
			url.search = `?${queryString}`;

			const src: string = url.toString();

			return {
				src,
				origin: url.origin
			};
		},
		[
			configuredSize.horizontal,
			configuredSize.vertical,
			hostUrl,
			theme
		]
	);

	const loadHandler = useCallback(
		(): void => {
			const { current: iframe } = ref;

			if (!iframe) {
				return;
			}

			const message: LoadHtmlMessage = {
				kind: "load-html",
				html
			};

			iframe.contentWindow
				?.postMessage(
					message,
					origin
				);
		},
		[origin, html]
	);

	useEffect(
		(): (() => void) => {
			const messageHandler = (args: MessageEvent) => {
				const { current: iframe } = ref;

				if (!iframe?.contentWindow) {
					return;
				}

				if (args.source !== iframe.contentWindow) {
					return;
				}

				if (args.origin !== origin) {
					return;
				}

				if (!args.data) {
					return;
				}

				let data: unknown;
				if (typeof args.data === "string") {
					try {
						data = JSON.parse(args.data);
					} catch (e) {
						console.error(e);
					}
				} else if (typeof args.data === "object") {
					data = args.data;
				} else {
					return;
				}

				if (!isSizeChangedMessage(data)) {
					return;
				}

				setContentSize({
					height: data.height,
					width: data.width
				});
			};

			window.addEventListener("message", messageHandler);
			return () => window.removeEventListener("message", messageHandler);
		},
		[origin]
	);

	useEffect(
		(): void => {
			// The implementation of this component relies on the onLoad event. When only the html
			// prop changes, it will not cause the iframe to load resulting in no onLoad event to fire.
			// So we trigger it ourself by setting the src attribute again.
			let shouldForceReload: boolean = false;

			if (forceReloadTrackerRef.current !== null) {
				const {
					src: previousSrc,
					html: previousHtml
				} = forceReloadTrackerRef.current;

				shouldForceReload = src === previousSrc && html !== previousHtml;
			}

			if (shouldForceReload) {
				ensureNotNil(ref.current)
					.src = src;
			}

			forceReloadTrackerRef.current = { src, html };
		},
		[html, src]
	);

	return (
		<IFrame
			src={src}
			ref={ref}
			style={{
				width: configuredSize.horizontal === "content-size"
					? contentSize.width
					: undefined,
				height: configuredSize.vertical === "content-size"
					? contentSize.height
					: undefined
			}}
			className={classNames(
				styles.iframe,
				{
					[styles.iframe_stretchedX]: configuredSize.horizontal === "stretch",
					[styles.iframe_stretchedY]: configuredSize.vertical === "stretch"
				}
			)}
			trackingId={trackingId}
			permissions={iframePermissions}
			onLoad={loadHandler}
		/>
	);
};
UserHtmlIframe.displayName = "UserHtmlIframe";

function useUnrequestedFocusGuard(ref: RefObject<HTMLIFrameElement | null>): void {
	useEffect(
		(): EffectWithDestructorResult => {
			const { current: iframe } = ref;

			if (!iframe) {
				return;
			}

			return unrequestedFocusGuard.register(iframe);
		},
		[ref]
	);
}

type UnrequestedFocusGuard = {
	register: (iframe: HTMLIFrameElement) => (() => void);
};

const unrequestedFocusGuard: UnrequestedFocusGuard = createUnrequestedFocusGuard();

function createUnrequestedFocusGuard(): UnrequestedFocusGuard {
	const iframes = new Set<HTMLIFrameElement>();
	let scrollPositions = new WeakMap<Element, ScrollPosition>();
	let trackedActiveElement: Element | null = null;
	let lastRequestedlyFocusedElement: Element | null = null;
	const unrequestedlyFocusedIframes = new Set<HTMLIFrameElement>();
	let hoveredIframe: HTMLIFrameElement | null = null;
	let lastPointerPosition: Coordinate | null = null;
	let isTabNavigationPending: boolean = false;
	let iframesWithReturnedFocus = new WeakSet<HTMLIFrameElement>();
	let focusPollingHandle: number | undefined;
	let cancellation: AbortController | undefined;

	const findRegisteredIframe = (element: Element | null): HTMLIFrameElement | null => {
		return element instanceof HTMLIFrameElement && iframes.has(element)
			? element
			: null;
	};

	const trackFocusChange = async (): Promise<void> => {
		const { activeElement } = document;

		if (activeElement === trackedActiveElement) {
			return;
		}

		trackedActiveElement = activeElement;

		const focusedIframe: HTMLIFrameElement | null = findRegisteredIframe(activeElement);
		const isFocusRequestedByUser: boolean = focusedIframe === hoveredIframe || isTabNavigationPending;

		isTabNavigationPending = false;

		if (!focusedIframe || isFocusRequestedByUser) {
			lastRequestedlyFocusedElement = activeElement;

			if (focusedIframe) {
				unrequestedlyFocusedIframes.clear();
			}

			return;
		}

		unrequestedlyFocusedIframes.add(focusedIframe);

		// The iframe runs in a different process, so the scroll that its focus causes can arrive
		// in the parent one frame after the focus change. Embedded content can also call focus()
		// a second time directly after the first call. We wait two frames, so that we restore
		// these scrolls and only return the focus after the embedded content stops taking it.
		await waitForAnimationFrames(2);

		if (document.activeElement === focusedIframe) {
			returnStolenFocus(focusedIframe);
		}
	};

	const returnStolenFocus = (iframe: HTMLIFrameElement): void => {
		if (iframesWithReturnedFocus.has(iframe)) {
			return;
		}

		iframesWithReturnedFocus.add(iframe);

		if (
			lastRequestedlyFocusedElement instanceof HTMLElement
			&& lastRequestedlyFocusedElement !== document.body
			&& lastRequestedlyFocusedElement.isConnected
		) {
			lastRequestedlyFocusedElement.focus({ preventScroll: true });
		} else {
			iframe.blur();
		}

		trackedActiveElement = document.activeElement;
	};

	const resetReturnedFocus = (): void => {
		iframesWithReturnedFocus = new WeakSet<HTMLIFrameElement>();
	};

	const pollFocusChange = (): void => {
		trackFocusChange();
		focusPollingHandle = requestAnimationFrame(pollFocusChange);
	};

	const stopPollingFocusChange = (): void => {
		if (focusPollingHandle !== undefined) {
			cancelAnimationFrame(focusPollingHandle);
			focusPollingHandle = undefined;
		}
	};

	const pointerMoveHandler = (args: PointerEvent): void => {
		lastPointerPosition = {
			x: args.clientX,
			y: args.clientY
		};
	};

	const hasPointerMovedTo = (position: Coordinate): boolean => {
		return lastPointerPosition !== null
			&& (position.x !== lastPointerPosition.x || position.y !== lastPointerPosition.y);
	};

	const userInteractionHandler = (): void => {
		unrequestedlyFocusedIframes.clear();
		resetReturnedFocus();
	};

	const keyDownHandler = (args: KeyboardEvent): void => {
		isTabNavigationPending = args.key === "Tab";
		userInteractionHandler();
	};

	const focusInHandler = (): void => {
		isTabNavigationPending = false;
	};

	const windowBlurHandler = (): void => {
		stopPollingFocusChange();
		focusPollingHandle = requestAnimationFrame(pollFocusChange);
		trackFocusChange();
	};

	const windowFocusHandler = (): void => {
		stopPollingFocusChange();
	};

	const windowScrollHandler = (args: Event): void => {
		const scrolledElement: EventTarget | Element | null = args.target === document
			? document.scrollingElement
			: args.target;

		if (!(scrolledElement instanceof Element)) {
			return;
		}

		trackFocusChange();

		const previousScrollPosition: ScrollPosition = scrollPositions.get(scrolledElement)
			?? captureScrollPosition(scrolledElement);

		const currentScrollPosition: ScrollPosition = captureScrollPosition(scrolledElement);

		const iframeBroughtIntoView: HTMLIFrameElement | undefined = [...unrequestedlyFocusedIframes].find(
			(x) => scrolledElement.contains(x)
				&& !isVisibleAtScrollPosition(x, scrolledElement, previousScrollPosition)
				&& isVisibleAtScrollPosition(x, scrolledElement, currentScrollPosition)
		);

		if (iframeBroughtIntoView) {
			restoreAncestorScrollPositions(iframeBroughtIntoView);
			return;
		}

		scrollPositions.set(scrolledElement, captureScrollPosition(scrolledElement));
	};

	const isVisibleAtScrollPosition = (
		element: Element,
		scrolledElement: Element,
		scrollPosition: ScrollPosition
	): boolean => {
		const viewport: DOMRect = scrolledElement === document.scrollingElement
			? new DOMRect(0, 0, window.innerWidth, window.innerHeight)
			: scrolledElement.getBoundingClientRect();

		const {
			top,
			left,
			width,
			height
		} = element.getBoundingClientRect();

		const topAtScrollPosition: number = top + scrolledElement.scrollTop - scrollPosition.top;
		const leftAtScrollPosition: number = left + scrolledElement.scrollLeft - scrollPosition.left;

		return topAtScrollPosition < viewport.bottom
			&& topAtScrollPosition + height > viewport.top
			&& leftAtScrollPosition < viewport.right
			&& leftAtScrollPosition + width > viewport.left;
	};

	const restoreAncestorScrollPositions = (element: Element): void => {
		let ancestor: Element | null = element.parentElement;

		while (ancestor) {
			const scrollPosition: ScrollPosition | undefined = scrollPositions.get(ancestor);

			if (scrollPosition) {
				ancestor.scrollTo({
					...scrollPosition,
					behavior: "instant"
				});
			}

			ancestor = ancestor.parentElement;
		}
	};

	const start = (): void => {
		cancellation = new AbortController();
		const { signal } = cancellation;

		trackedActiveElement = document.activeElement;
		lastRequestedlyFocusedElement = document.activeElement;

		window.addEventListener(
			"keydown",
			keyDownHandler,
			{
				capture: true,
				passive: true,
				signal
			}
		);

		for (const eventName of userInteractionEventNames) {
			window.addEventListener(
				eventName,
				userInteractionHandler,
				{
					capture: true,
					passive: true,
					signal
				}
			);
		}

		window.addEventListener(
			"pointermove",
			pointerMoveHandler,
			{
				capture: true,
				passive: true,
				signal
			}
		);

		document.addEventListener(
			"focusin",
			focusInHandler,
			{ signal }
		);

		window.addEventListener(
			"blur",
			windowBlurHandler,
			{ signal }
		);

		window.addEventListener(
			"focus",
			windowFocusHandler,
			{ signal }
		);

		document.addEventListener(
			"scroll",
			windowScrollHandler,
			{
				capture: true,
				signal
			}
		);
	};

	const stop = (): void => {
		cancellation?.abort();
		stopPollingFocusChange();
		scrollPositions = new WeakMap<Element, ScrollPosition>();
		unrequestedlyFocusedIframes.clear();
		isTabNavigationPending = false;
		resetReturnedFocus();
	};

	return {
		register: (iframe: HTMLIFrameElement): (() => void) => {
			if (iframes.size === 0) {
				start();
			}

			iframes.add(iframe);

			let ancestor: Element | null = iframe.parentElement;

			while (ancestor) {
				if (!scrollPositions.has(ancestor)) {
					scrollPositions.set(ancestor, captureScrollPosition(ancestor));
				}

				ancestor = ancestor.parentElement;
			}

			const iframeCancellation = new AbortController();

			iframe.addEventListener(
				"pointerenter",
				(args: PointerEvent): void => {
					if (hasPointerMovedTo({ x: args.clientX, y: args.clientY })) {
						hoveredIframe = iframe;
					}
				},
				{ signal: iframeCancellation.signal }
			);

			iframe.addEventListener(
				"pointerleave",
				(): void => {
					if (hoveredIframe === iframe) {
						hoveredIframe = null;
					}
				},
				{ signal: iframeCancellation.signal }
			);

			return (): void => {
				iframeCancellation.abort();
				iframes.delete(iframe);

				if (hoveredIframe === iframe) {
					hoveredIframe = null;
				}

				unrequestedlyFocusedIframes.delete(iframe);

				if (iframes.size === 0) {
					stop();
				}
			};
		}
	};
}

const userInteractionEventNames: readonly (keyof WindowEventMap)[] = [
	"pointerdown",
	"wheel",
	"touchstart"
];

type ScrollPosition = {
	top: number;
	left: number;
};
function captureScrollPosition(element: Element): ScrollPosition {
	return {
		top: element.scrollTop,
		left: element.scrollLeft
	};
}
type LoadHtmlMessage = {
	kind: "load-html";
	html: string;
};

type SizeChangedMessage = {
	kind: "size-changed";
	height: number | undefined;
	width: number | undefined;
};

function isSizeChangedMessage(data: unknown): data is SizeChangedMessage {
	if (!data || typeof data !== "object") {
		return false;
	}

	const message = (data as SizeChangedMessage);

	// NOTE: strict validation because this data comes from an external system
	return message.hasOwnProperty("kind") && message.kind === "size-changed"
		&& message.hasOwnProperty("height") && (typeof message.height === "undefined" || typeof message.height === "number")
		&& message.hasOwnProperty("width") && (typeof message.width === "undefined" || typeof message.width === "number");
}

const iframePermissions: readonly IFramePermission[] = [
	IFramePermission.Downloads,
	IFramePermission.Forms,
	IFramePermission.Modals,
	IFramePermission.OrientationLock,
	IFramePermission.PointerLock,
	IFramePermission.Popups,
	IFramePermission.EscapingPopups,
	IFramePermission.Presentation,
	IFramePermission.SameOrigin,
	IFramePermission.Scripts
];

type ForceReloadTrackerState = {
	src: string;
	html: string;
};
